/**
 * Transport der Wiedergabe: Zustandsmaschine über Laden, Abspielen, Stoppen
 * und Loop. Koordiniert Sample-Loader, Web-Audio-Adapter und Scheduler und
 * meldet seinen Zustand samt sichtbarer Abspielposition an Interessenten.
 *
 * Die browser-spezifischen Bausteine (Engine, Ticker, Frame-Schleife) werden
 * injiziert und sind über schmale Interfaces abstrahiert – der Transport ist
 * damit ohne echten AudioContext unit-testbar.
 */

import type { DrumPattern } from '../model/DrumPattern';
import type { DrumInstrument } from '../model/types';
import type { AudioEngine } from './engine';
import { playheadStep } from './playhead';
import { scheduleAudioEvents } from './schedule';
import {
  IntervalTicker,
  Scheduler,
  type SampleMap,
  type Ticker,
} from './Scheduler';
import { SampleLoader } from './samples';
import {
  TOTAL_STEPS,
  patternDurationSeconds,
  stepDurationSeconds,
} from './timing';

export type PlaybackState =
  'idle' | 'loading' | 'playing' | 'stopped' | 'error';

export interface TransportSnapshot {
  readonly state: PlaybackState;
  readonly loop: boolean;
  readonly playheadStep: number | null;
  readonly failedInstruments: readonly DrumInstrument[];
}

export type TransportListener = (snapshot: TransportSnapshot) => void;

/** Regelmäßige Bildschirmaktualisierung für die Abspielposition. */
export interface FrameLoop {
  start(callback: () => void): void;
  stop(): void;
}

/** Standard-Frame-Schleife auf Basis von requestAnimationFrame. */
export class RafFrameLoop implements FrameLoop {
  #id: number | null = null;
  #running = false;

  start(callback: () => void): void {
    this.stop();
    this.#running = true;
    const loop = () => {
      if (!this.#running) {
        return;
      }
      callback();
      this.#id = requestAnimationFrame(loop);
    };
    this.#id = requestAnimationFrame(loop);
  }

  stop(): void {
    this.#running = false;
    if (this.#id !== null) {
      cancelAnimationFrame(this.#id);
      this.#id = null;
    }
  }
}

export type SchedulerFactory = (
  engine: AudioEngine,
  samples: SampleMap,
) => Scheduler;

export interface AudioTransportOptions {
  readonly engine: AudioEngine;
  readonly loader: SampleLoader;
  /** Liefert das aktuell zu spielende Pattern (Snapshot-Quelle). */
  readonly getPattern: () => DrumPattern;
  /** Liefert das aktuelle Tempo (BPM). */
  readonly getBpm: () => number;
  readonly ticker?: Ticker;
  readonly frameLoop?: FrameLoop;
  readonly createScheduler?: SchedulerFactory;
}

export class AudioTransport {
  readonly #engine: AudioEngine;
  readonly #loader: SampleLoader;
  readonly #getPattern: () => DrumPattern;
  readonly #getBpm: () => number;
  readonly #ticker: Ticker;
  readonly #frameLoop: FrameLoop;
  readonly #createScheduler: SchedulerFactory;
  readonly #listeners = new Set<TransportListener>();

  #state: PlaybackState = 'idle';
  #loop = false;
  #playheadStep: number | null = null;
  #failed: readonly DrumInstrument[] = [];

  #scheduler: Scheduler | null = null;
  #stepDuration = 0;
  #patternDuration = 0;

  constructor(options: AudioTransportOptions) {
    this.#engine = options.engine;
    this.#loader = options.loader;
    this.#getPattern = options.getPattern;
    this.#getBpm = options.getBpm;
    this.#ticker = options.ticker ?? new IntervalTicker();
    this.#frameLoop = options.frameLoop ?? new RafFrameLoop();
    this.#createScheduler =
      options.createScheduler ??
      ((engine, samples) => new Scheduler(engine, samples, this.#ticker));
  }

  get state(): PlaybackState {
    return this.#state;
  }

  get loop(): boolean {
    return this.#loop;
  }

  snapshot(): TransportSnapshot {
    return {
      state: this.#state,
      loop: this.#loop,
      playheadStep: this.#playheadStep,
      failedInstruments: [...this.#failed],
    };
  }

  /** Registriert einen Listener und ruft ihn einmalig mit dem Zustand auf. */
  subscribe(listener: TransportListener): () => void {
    this.#listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Startet die Wiedergabe am Patternanfang. Muss aus einer Benutzeraktion
   * heraus aufgerufen werden (Autoplay-Freigabe). Wiederholte oder parallele
   * Aufrufe während Laden/Wiedergabe werden ignoriert.
   */
  async play(): Promise<void> {
    if (this.#state === 'loading' || this.#state === 'playing') {
      return;
    }
    this.#setState('loading');

    try {
      await this.#engine.resume();
    } catch (error) {
      console.error('AudioContext konnte nicht aktiviert werden.', error);
      this.#setState('error');
      return;
    }

    let failed: readonly DrumInstrument[];
    let buffers: SampleMap;
    try {
      const result = await this.#loader.load();
      failed = result.failed;
      buffers = result.buffers;
    } catch (error) {
      console.error('Samples konnten nicht geladen werden.', error);
      this.#setState('error');
      return;
    }

    // Während des Ladens könnte gestoppt worden sein (z. B. per Stop-Klick).
    // Zugriff über den Getter, damit die Zustandsprüfung nicht durch die
    // Ablauf-Narrowing-Analyse verkürzt wird.
    if (this.state !== 'loading') {
      return;
    }

    this.#failed = failed;
    if (buffers.size === 0) {
      this.#setState('error');
      return;
    }

    const bpm = this.#getBpm();
    const pattern = this.#getPattern();
    const events = scheduleAudioEvents(pattern, bpm);
    this.#stepDuration = stepDurationSeconds(bpm) / 3;
    this.#patternDuration = patternDurationSeconds(bpm);

    this.#scheduler = this.#createScheduler(this.#engine, buffers);
    this.#scheduler.setLoop(this.#loop);
    this.#scheduler.start(events, this.#patternDuration, this.#loop);

    this.#playheadStep = null;
    this.#setState('playing');
    this.#frameLoop.start(this.#onFrame);
  }

  /** Stoppt die Wiedergabe unmittelbar und setzt die Position zurück. */
  stop(): void {
    this.#scheduler?.stop();
    this.#scheduler = null;
    this.#frameLoop.stop();
    this.#playheadStep = null;
    this.#setState('stopped');
  }

  /** Schaltet den Loop-Modus. */
  setLoop(loop: boolean): void {
    this.#loop = loop;
    this.#scheduler?.setLoop(loop);
    this.#notify();
  }

  #onFrame = (): void => {
    if (this.#state !== 'playing' || !this.#scheduler) {
      return;
    }
    const elapsed = this.#scheduler.elapsed();
    if (!this.#loop && elapsed >= this.#patternDuration) {
      this.stop();
      return;
    }
    const tick = playheadStep(
      elapsed,
      this.#stepDuration,
      TOTAL_STEPS * 3,
      this.#loop,
    );
    const step = tick === null ? null : tick / 3;
    if (step !== this.#playheadStep) {
      this.#playheadStep = step;
      this.#notify();
    }
  };

  #setState(state: PlaybackState): void {
    if (this.#state === state) {
      return;
    }
    this.#state = state;
    this.#notify();
  }

  #notify(): void {
    const snapshot = this.snapshot();
    for (const listener of this.#listeners) {
      listener(snapshot);
    }
  }
}
